/* eslint-disable @nx/enforce-module-boundaries */
import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep, shuffle } from 'lodash';
import {
  catchError,
  concatMap,
  exhaustMap,
  EMPTY,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';

import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { Team } from '@ninety/platform/models/_shared/team';
import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { ItemType } from '@ninety/platform/models/enums/item-type';
import { MeetingAgenda } from '@ninety/platform/models/meetings/meeting-agenda';
import { MeetingBot, MeetingBotStatus } from '@ninety/platform/models/meetings/meeting-bot';
import { MeetingMessageAction } from '@ninety/platform/models/meetings/meeting-message-action';
import { MeetingRoutes } from '@ninety/platform/models/meetings/meeting-routes';
import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import { ErrorService, FilterService, ChannelService, StateService, TeamsApiService } from '@ninety/platform/services';
import {
  DetailViewActions,
  MeetingIssuesEvents,
  TeamSelectors,
  selectCompanyId,
  selectIsAgreementsBasedTodosCompany,
  NotificationActions,
  SpinnerActions,
  RealTimeActions,
  TeamListStateActions,
  selectCurrentUserIsObserver,
  selectCurrentUserId,
  selectUserNameById,
  selectLanguage,
} from '@ninety/platform/state';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import { ConfirmDialogComponent, ConfirmDialogData, WarningConfirmDialogComponent } from '@ninety/ui/core';
import { FileService, UserService } from '@ninety/ui/legacy/core/index';
import {
  MeetingAgendaPrintParams,
  PrintApi,
  PrintOptions,
  PrintService,
} from '@ninety/ui/legacy/core/services/print.service';
import { environment } from '@ninety/ui/web/environments';
import { getDefaultActionParams } from '@ninety/web/pages/accountability-chart/_state/chart/effects/seat.effects';
import { TopToolbarActions } from '@ninety/web/pages/layouts/_state/top-toolbar/top-toolbar-state.actions';
import { CreateDialogService } from '@ninety/web/pages/layouts/services/create-dialog.service';
import { MeetingBotService } from '@ninety/web/pages/meeting/_shared/services/meeting-bot.service';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';
import { FilterBarActions } from '@ninety/web/pages/shared/layouts';

import { AgendaV2Service } from '../../_services/agenda-v2.service';
import { MeetingsV2Service } from '../../_services/meetings-v2.service';
import { AddMeetingBotDialogComponent } from '../../_shared/add-meeting-bot-dialog/add-meeting-bot-dialog.component';
import { PrintAgendasDialogComponent } from '../../meetings-page/print-meeting-agenda/print-meeting-agenda.component';
import { StartMeetingDialogComponent } from '../../meetings-page/start-meeting-dialog/start-meeting-dialog.component';
import {
  MeetingConcludeActions,
  MeetingDialogActions,
  MeetingIssuesActions,
  MeetingNotesActions,
  MeetingRealTimeActions,
  MeetingsAgendasV2Actions,
  MeetingSchedulingActions,
  MeetingsPageActions,
  MeetingsTabsActions,
  MeetingStateActions,
  PastMeetingsPageActions,
} from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

const LOCAL_DEV_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];

@Injectable()
export class MeetingsPageEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly meetingsV2Service = inject(MeetingsV2Service);
  private readonly meetingService = inject(MeetingService);
  private readonly meetingBotService = inject(MeetingBotService);
  private readonly router = inject(Router);
  private readonly errorService = inject(ErrorService);
  private readonly dialog = inject(MatDialog);
  private readonly channelService = inject(ChannelService);
  private readonly printService = inject(PrintService);
  private readonly createDialogService = inject(CreateDialogService);
  private readonly filterService = inject(FilterService);
  private readonly userService = inject(UserService);
  private readonly teamApiService = inject(TeamsApiService);
  private readonly agendaV2Service = inject(AgendaV2Service);
  private readonly stateService = inject(StateService); // for setTitle()
  private readonly fileService = inject(FileService);

  /**
   * Copy the new attachment list onto MeetingService's own meeting.
   *
   * `setCurrentMeeting` sends the service's whole meeting to the store, so if we only updated
   * state the next save or timer tick would put the old attachments back.
   */
  syncAttachmentsToMeetingService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.attachmentAdded, MeetingNotesActions.attachmentRemoved),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
        tap(([, meeting]) => {
          if (this.meetingService.currentMeeting) {
            this.meetingService.currentMeeting.attachments = meeting?.attachments ?? [];
          }
        })
      ),
    { dispatch: false }
  );

  setPageTitle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsTabsActions.init),
        concatLatestFrom(() => [this.store.select(selectLanguage)]),
        map(([, language]) => this.stateService.setTitle(language.meeting.route))
      ),
    { dispatch: false }
  );

  hideToolbar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.init),
      map(() => TopToolbarActions.toolbarHidden({ toolbarHidden: true }))
    )
  );

  hideFilterBar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.init),
      map(() => FilterBarActions.hide())
    )
  );

  stopSpinnerOnInit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.init),
      map(() => SpinnerActions.stopPrimary({ source: 'Meetings Page Init' }))
    )
  );

  showToolbar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.reset),
      map(() => TopToolbarActions.toolbarHidden({ toolbarHidden: false }))
    )
  );

  showFilterBar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsTabsActions.reset),
      map(() => FilterBarActions.show())
    )
  );

  openUniversalCreateDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.openUniversalCreateModal),
        switchMap(_ => this.createDialogService.open({ itemType: ItemType.headline }))
      ),
    { dispatch: false }
  );

  teamSelected$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.selectTeam, MeetingsAgendasV2Actions.selectTeam, PastMeetingsPageActions.selectTeam),
        tap(({ team }) => {
          window.sessionStorage.setItem('lastAccessedTeamId', team._id);
          //NOTE: this might be needed but I couldn't find why, keeping for now, remove in 6 months if no features require it
          // this.stateService.currentCompanyUser$.value.lastAccessedTeamId = team._id;
          this.userService.update({ lastAccessedTeamId: team._id }).subscribe();
          this.filterService.setTeamId(team._id);
        })
      ),
    { dispatch: false }
  );

  loadPastMeetings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PastMeetingsPageActions.selectTeam,
        PastMeetingsPageActions.selectMeetingType,
        PastMeetingsPageActions.paginationChange,
        PastMeetingsPageActions.sortChange,
        MeetingsPageActions.deleteMeetingSuccess,
        MeetingsPageActions.getActiveMeetingInfo,
        MeetingsPageActions.selectTeam
      ),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectTeamId)]),
      filter(([, teamId]) => !!teamId),
      concatMap(() => of(PastMeetingsPageActions.loadPastMeetings()))
    )
  );

  getMeetingSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.selectTeam),
      map(_ => MeetingSchedulingActions.getMeetingSchedules())
    )
  );

  getPastMeetings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PastMeetingsPageActions.loadPastMeetings),
      concatLatestFrom(() => [
        this.store.select(selectCompanyId),
        this.store.select(MeetingsStateSelectors.selectPage),
        this.store.select(MeetingsStateSelectors.selectSort),
        this.store.select(MeetingsStateSelectors.selectFilters),
      ]),
      switchMap(([_, companyId, page, sort, { meetingType, agendaId, teamId }]) =>
        this.meetingsV2Service
          .getPastMeetings(companyId, teamId, {
            pageIndex: page.index,
            pageSize: page.size,
            sortDirection: sort.direction,
            sortField: sort.field,
            meetingType,
            agendaId,
          })
          .pipe(
            map(pastMeetingsResponse => PastMeetingsPageActions.loadPastMeetingsSuccess({ pastMeetingsResponse })),
            catchError((error: unknown) => of(PastMeetingsPageActions.loadPastMeetingsFailure({ error })))
          )
      )
    )
  );

  getActiveMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingsPageActions.selectTeam,
        MeetingsPageActions.getActiveMeetingInfo,
        MeetingsPageActions.deleteActiveMeetingSuccess,
        MeetingsPageActions.deleteMeetingSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(selectCompanyId),
        this.store.select(MeetingsStateSelectors.selectTeamId),
      ]),
      filter(([, companyId, teamId]) => !!companyId && !!teamId),
      switchMap(([, companyId, teamId]) =>
        this.meetingsV2Service.getActiveMeetingInfo(companyId, teamId).pipe(
          map(response => MeetingsPageActions.getActiveMeetingInfoSuccess({ meeting: response.meeting })),
          catchError((error: unknown) => of(MeetingsPageActions.getActiveMeetingInfoFailure({ error })))
        )
      )
    )
  );

  notifyOnDeleteActiveMeetingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteActiveMeetingSuccess),
      map(_ => NotificationActions.notify({ message: 'Delete successful' }))
    )
  );

  notifyOnDeleteActiveMeetingFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteActiveMeetingFailure),
      map(({ error }) => NotificationActions.notifyError({ error, message: 'Delete failed. Please try again.' }))
    )
  );

  navigateHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsTabsActions.navigate),
        tap(({ route }) => {
          this.router.navigate([`/meetings/${route}`]);
        })
      ),
    { dispatch: false }
  );

  loadPastMeetingsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PastMeetingsPageActions.loadPastMeetingsFailure),
        concatLatestFrom(() => [this.store.select(selectLanguage)]),
        tap(([error, language]) => {
          this.errorService.notify(error, `Could load ${language.meeting.items}. Please try again.`);
        })
      ),
    { dispatch: false }
  );

  /**
   * Updates team/meeting settings to persist auto-archive and send-recap settings when completing a meeting
   */
  updateMeetingSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingConcludeActions.updateMeetingSettings),
      concatLatestFrom(() => [this.store.select(selectIsAgreementsBasedTodosCompany)]),
      switchMap(([action, isAgreementsBasedTodosCompany]) =>
        this.teamApiService.getTeamById(action.teamId).pipe(
          map(team => ({ ...action, team, isAgreementsBasedTodosCompany })),
          filter(
            ({ team, autoArchive, shouldSendRecap, acceptPendingAgreementBasedTodos, showIssueDescOnRecapEmail }) =>
              this.meetingSettingsHaveChanges(
                team,
                autoArchive,
                shouldSendRecap,
                acceptPendingAgreementBasedTodos,
                showIssueDescOnRecapEmail
              )
          ),
          map(
            ({
              team,
              autoArchive,
              shouldSendRecap,
              acceptPendingAgreementBasedTodos,
              isAgreementsBasedTodosCompany,
              showIssueDescOnRecapEmail,
            }) => {
              const update = {
                sendMeetingRecapEmail: shouldSendRecap,
                ...(isAgreementsBasedTodosCompany ? { acceptPendingAgreementBasedTodos } : {}),
                autoArchive,
                showIssueDescOnRecapEmail,
              };
              return TeamListStateActions.updateSettings({ teamId: team._id, update });
            }
          )
        )
      )
    )
  );

  persistSegueAbsences$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingConcludeActions.syncSegueAbsences),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeetingAndIsPresenter)),
        filter(([, { meeting, isPresenter }]) => !!meeting && !!this.meetingService.currentMeeting && isPresenter),
        concatMap(([, { meeting }]) =>
          this.meetingService
            .updateCurrentMeeting({
              userRatings: meeting.userRatings.map(r => ({ ...r })),
              absences: [...meeting.absences],
            })
            .pipe(
              // Defends against the HTTP PATCH itself hanging (e.g. a connectivity blip). Without
              // this, a single stuck call would freeze every later segue-absence sync for the rest
              // of the meeting behind it, since this effect processes actions strictly in order.
              timeout(10_000),
              catchError((_error: unknown) => of(null))
            )
        )
      ),
    { dispatch: false }
  );

  /**
   * Tells the other clients that segue absences were synced.
   *
   * Separate from {@link persistSegueAbsences$} — same reasoning as {@link broadcastMeetingPause$} —
   * so a slow/stuck PATCH there can't block this best-effort broadcast, and a failed broadcast can't
   * suppress the persist. Both effects react to the same syncSegueAbsences action independently.
   */
  broadcastSegueAbsences$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingConcludeActions.syncSegueAbsences),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeetingAndIsPresenter)),
        filter(([, { meeting, isPresenter }]) => !!meeting && isPresenter),
        mergeMap(([, { meeting }]) =>
          this.meetingService.broadcastMessage({
            messageType: 'shuffle-users',
            document: { userRatings: meeting.userRatings, absences: meeting.absences },
          })
        )
      ),
    { dispatch: false }
  );

  openStartMeetingDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingDialogActions.openStartMeetingDialog),
        tap(() => {
          this.dialog.open(StartMeetingDialogComponent);
        })
      ),
    { dispatch: false }
  );

  createMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingDialogActions.createMeeting),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectTeamId)]),
      switchMap(([{ meetingType, agendaId, meetingBot, meetingScheduleId, botLinked }, teamId]) => {
        const meetingHasBot = !!meetingBot?.extMeetingUrl || !!botLinked;
        return this.meetingService
          .createNewMeeting({
            type: meetingType === MeetingType.annualDayTwo ? MeetingType.annualDayOne : meetingType,
            teamId,
            agendaId,
            botLinked: meetingHasBot,
          })
          .pipe(
            map(meeting =>
              MeetingDialogActions.createMeetingSuccess({
                meeting: cloneDeep({ ...meeting, meetingBot, botLinked: meetingHasBot }),
                meetingScheduleId,
              })
            ),
            catchError((error: unknown) => of(MeetingDialogActions.createMeetingFailure({ error })))
          );
      })
    )
  );

  createMeetingBot$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingDialogActions.createMeetingSuccess),
        filter(({ meeting }) => !!meeting.meetingBot?.extMeetingUrl),
        mergeMap(({ meeting }) => {
          const meetingBot = meeting.meetingBot as MeetingBot;
          return this.meetingBotService
            .createBot({
              extMeetingUrl: meetingBot.extMeetingUrl,
              ninetyMeetingUrl: this.ninetyMeetingUrlFor(meeting._id),
              meetingId: meeting._id,
              meetingType: meeting.type,
            })
            .pipe(
              map(({ botId }) => ({
                ...meetingBot,
                botId,
                status: botId ? MeetingBotStatus.active : MeetingBotStatus.failed,
              })),
              catchError((error: unknown) => {
                this.errorService.notify(error, 'Could not add Maz to the meeting.');
                return of({ ...meetingBot, status: MeetingBotStatus.failed });
              }),
              switchMap(updatedMeetingBot =>
                this.meetingService.currentMeeting$.pipe(
                  filter(currentMeeting => currentMeeting?._id === meeting._id),
                  take(1),
                  timeout({ first: 10_000 }),
                  switchMap(() => this.meetingService.updateCurrentMeeting({ meetingBot: updatedMeetingBot }, true)),
                  catchError((error: unknown) => {
                    this.errorService.notify(error, 'Could not attach Maz to the meeting.');
                    return EMPTY;
                  })
                )
              )
            );
        })
      ),
    { dispatch: false }
  );

  openAddMeetingBotDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.openAddMeetingBotDialog),
      exhaustMap(() => {
        const dialogRef = this.dialog.open<AddMeetingBotDialogComponent, undefined, string | undefined>(
          AddMeetingBotDialogComponent
        );

        return dialogRef.afterClosed().pipe(
          filter((extMeetingUrl): extMeetingUrl is string => !!extMeetingUrl),
          map(extMeetingUrl => MeetingStateActions.addMeetingBot({ extMeetingUrl }))
        );
      })
    )
  );

  addMeetingBot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.addMeetingBot),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      filter(([, currentMeeting]) => !!currentMeeting?._id),
      exhaustMap(([{ extMeetingUrl }, currentMeeting]) =>
        this.meetingBotService
          .createBot({
            extMeetingUrl,
            ninetyMeetingUrl: this.ninetyMeetingUrlFor(currentMeeting._id),
            meetingId: currentMeeting._id,
            meetingType: currentMeeting.type,
          })
          .pipe(
            switchMap(({ botId }) =>
              this.meetingService
                .updateCurrentMeeting(
                  {
                    meetingBot: {
                      extMeetingUrl,
                      botId,
                      status: botId ? MeetingBotStatus.active : MeetingBotStatus.failed,
                    },
                    ...(botId ? { botLinked: true } : null),
                  },
                  true
                )
                .pipe(
                  // The `true` above is local only, so save botLinked. A failed save already toasts, so don't
                  // also report it as "add failed".
                  switchMap(() =>
                    botId
                      ? this.meetingService
                          .updateMeetingHttp(currentMeeting._id, { botLinked: true })
                          .pipe(catchError(() => EMPTY))
                      : EMPTY
                  )
                )
            ),
            switchMap(() => EMPTY),
            catchError((error: unknown) => of(MeetingStateActions.addMeetingBotFailure({ error })))
          )
      )
    )
  );

  notifyOnAddMeetingBotFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.addMeetingBotFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          error,
          message: 'Could not add Maz to the meeting. Please try again.',
        })
      )
    )
  );

  openRemoveMeetingBotDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.openRemoveMeetingBotDialog),
      exhaustMap(() => {
        const confirmRemoveDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(
          ConfirmDialogComponent,
          {
            data: {
              title: 'Are you sure?',
              message:
                'This will remove Maz from the meeting and stop any active recording. Transcripts cannot be added back to this meeting once removed.',
              confirmButtonText: 'Remove Maz',
              cancelButtonText: 'Cancel',
            },
          }
        );

        return confirmRemoveDialogRef.afterClosed().pipe(
          filter(removeConfirmed => !!removeConfirmed),
          map(() => MeetingStateActions.removeMeetingBot())
        );
      })
    )
  );

  removeMeetingBot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.removeMeetingBot),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      filter(
        ([, currentMeeting]) =>
          !!currentMeeting?.meetingBot?.botId && currentMeeting.meetingBot.status !== MeetingBotStatus.dismissed
      ),
      exhaustMap(([, currentMeeting]) =>
        this.meetingBotService.deleteBot(currentMeeting.meetingBot.botId).pipe(
          switchMap(() =>
            this.meetingService.updateCurrentMeeting(
              { meetingBot: { ...currentMeeting.meetingBot, status: MeetingBotStatus.dismissed } },
              true
            )
          ),
          switchMap(() => EMPTY),
          catchError((error: unknown) => of(MeetingStateActions.removeMeetingBotFailure({ error })))
        )
      )
    )
  );

  notifyOnRemoveMeetingBotFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.removeMeetingBotFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          error,
          message: 'Could not remove Maz from the meeting. Please try again.',
        })
      )
    )
  );

  pauseMeetingBot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.pauseMeetingBot),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      filter(
        ([, currentMeeting]) =>
          !!currentMeeting?.meetingBot?.botId && currentMeeting.meetingBot.status === MeetingBotStatus.recording
      ),
      exhaustMap(([, currentMeeting]) =>
        this.meetingBotService.pauseBot(currentMeeting.meetingBot.botId).pipe(
          switchMap(() =>
            this.meetingService.updateCurrentMeeting(
              { meetingBot: { ...currentMeeting.meetingBot, status: MeetingBotStatus.paused } },
              true
            )
          ),
          switchMap(() => EMPTY),
          catchError((error: unknown) => of(MeetingStateActions.pauseMeetingBotFailure({ error })))
        )
      )
    )
  );

  notifyOnPauseMeetingBotFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.pauseMeetingBotFailure),
      map(({ error }) => NotificationActions.notifyError({ error, message: 'Could not pause Maz. Please try again.' }))
    )
  );

  resumeMeetingBot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.resumeMeetingBot),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      filter(
        ([, currentMeeting]) =>
          !!currentMeeting?.meetingBot?.botId && currentMeeting.meetingBot.status === MeetingBotStatus.paused
      ),
      exhaustMap(([, currentMeeting]) =>
        this.meetingBotService.resumeBot(currentMeeting.meetingBot.botId).pipe(
          switchMap(() =>
            this.meetingService.updateCurrentMeeting(
              { meetingBot: { ...currentMeeting.meetingBot, status: MeetingBotStatus.recording } },
              true
            )
          ),
          switchMap(() => EMPTY),
          catchError((error: unknown) => of(MeetingStateActions.resumeMeetingBotFailure({ error })))
        )
      )
    )
  );

  notifyOnResumeMeetingBotFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.resumeMeetingBotFailure),
      map(({ error }) => NotificationActions.notifyError({ error, message: 'Could not resume Maz. Please try again.' }))
    )
  );

  // Only meaningful for a calendar-scheduled bot that hasn't joined yet (active/creating - see
  // notetaker-control's label()); no optimistic status change here, since Recall's actual next
  // status (joining, waiting room, recording) arrives normally via the webhook/refresh path.
  // Gated on currentMeetingScheduleId too - active/creating alone also matches a just-added ad-hoc
  // bot (see addMeetingBot$/createMeetingBot$), which has no scheduled record to join early.
  joinMeetingBotNow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.joinMeetingBotNow),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingScheduleId),
      ]),
      filter(
        ([, currentMeeting, meetingScheduleId]) =>
          !!meetingScheduleId &&
          !!currentMeeting?.meetingBot?.botId &&
          (currentMeeting.meetingBot.status === MeetingBotStatus.active ||
            currentMeeting.meetingBot.status === MeetingBotStatus.creating)
      ),
      exhaustMap(([, currentMeeting]) =>
        this.meetingBotService.joinNow(currentMeeting._id, this.ninetyMeetingUrlFor(currentMeeting._id)).pipe(
          switchMap(() => EMPTY),
          catchError((error: unknown) => {
            // A 404 means this bot was never calendar-scheduled (e.g. an ad-hoc bot) - there's
            // nothing to join early, so it's not a failure worth surfacing.
            if ((error as HttpErrorResponse)?.status === 404) return EMPTY;
            return of(MeetingStateActions.joinMeetingBotNowFailure({ error }));
          })
        )
      )
    )
  );

  notifyOnJoinMeetingBotNowFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.joinMeetingBotNowFailure),
      map(({ error }) =>
        NotificationActions.notifyError({
          error,
          message: 'Could not join Maz now. Please try again.',
        })
      )
    )
  );

  getMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.getMeeting),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectMeetingStatus)]),
      switchMap(([, meeting]) =>
        this.meetingsV2Service.startMeeting(meeting._id).pipe(
          map(meeting => MeetingsPageActions.getMeetingSuccess({ meeting })),
          catchError((error: unknown) => of(MeetingsPageActions.getMeetingFailure({ error })))
        )
      )
    )
  );

  joinActiveMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.joinActiveMeeting),
      switchMap(({ meeting }) =>
        this.meetingsV2Service.startMeeting(meeting._id).pipe(
          map(started => MeetingsPageActions.getMeetingSuccess({ meeting: started })),
          catchError((error: unknown) => of(MeetingsPageActions.getMeetingFailure({ error })))
        )
      )
    )
  );

  subscribeToMeetingChannelsOnJoin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.joinActiveMeeting),
        switchMap(({ meeting }) =>
          this.channelService.subscribeToMeetingChannels(`meeting-${meeting._id}`, meeting.teamId)
        )
      ),
    { dispatch: false }
  );

  getMeetingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.getMeetingSuccess, MeetingsPageActions.resumeMeetingSuccess),
      concatLatestFrom(() => [this.store.select(TeamSelectors.selectAll)]),
      tap(([{ meeting }, teams]) => {
        meeting = cloneDeep(meeting);
        this.meetingService.initializeMeeting(meeting);
        this.meetingService.team$.next(teams.find(t => t._id === meeting.teamId));
        this.meetingService.navigateToMeeting(meeting);
      }),
      map(([{ meeting }]) => MeetingRealTimeActions.checkMeetingPresenterExists({ meeting }))
    )
  );

  /** The same as getMeeting but will dispatch a meeting state change in response to resumeMeetingSuccess */
  resumeMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.resumeMeeting),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectMeetingStatus)]),
      switchMap(([, meeting]) =>
        this.meetingsV2Service.startMeeting(meeting._id).pipe(
          map(meeting => MeetingsPageActions.resumeMeetingSuccess({ meeting })),
          catchError((error: unknown) => of(MeetingsPageActions.resumeMeetingFailure({ error })))
        )
      )
    )
  );

  /** Makes the grant request to listen to each specific meeting section (rocks, todos, issues, etc.) */
  subscribeToMeetingChannels$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.getMeeting),
        concatLatestFrom(() => [
          this.store.select(MeetingsStateSelectors.selectMeetingStatus),
          this.store.select(MeetingsStateSelectors.selectTeamId),
        ]),
        switchMap(([, meeting, teamId]) =>
          this.channelService.subscribeToMeetingChannels(`meeting-${meeting._id}`, teamId)
        )
      ),
    { dispatch: false }
  );

  joinMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.getMeetingFailure),
        concatLatestFrom(() => [this.store.select(selectLanguage)]),
        tap(([e, { meeting }]) => {
          this.errorService.notify(e, `Could not get ${meeting.item}.  Please try again.`);
        })
      ),
    { dispatch: false }
  );

  openDeleteMeetingDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.openDeleteMeetingDialog),

      concatLatestFrom(() => [
        this.store.select(selectLanguage),
        this.store.select(MeetingsStateSelectors.selectMeetingStatus),
      ]),
      exhaustMap(([, language, { inProgress, paused }]) => {
        const message =
          inProgress && !paused
            ? `Deleting will remove all active participants from the ${language.meeting.item}.
            To save the ${language.meeting.item}, join and conclude. This action cannot be undone.`
            : '';

        const confirmDeleteDialogRef = this.dialog.open<WarningConfirmDialogComponent, ConfirmDialogData>(
          WarningConfirmDialogComponent,
          {
            data: {
              title: `Are you sure you want to delete this ${language.meeting.item}?`,
              message,
              confirmButtonText: 'Delete',
            },
          }
        );

        return confirmDeleteDialogRef
          .afterClosed()
          .pipe(
            map(deleteConfirmed =>
              deleteConfirmed
                ? MeetingsPageActions.deleteActiveMeeting()
                : MeetingsPageActions.cancelDeleteActiveMeeting()
            )
          );
      })
    )
  );

  deleteActiveMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteActiveMeeting),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectMeetingStatus),
        this.store.select(selectCompanyId),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      switchMap(([, meeting, companyId, teamId]) =>
        this.meetingsV2Service.deleteMeeting(meeting._id, companyId, teamId).pipe(
          map(() => MeetingsPageActions.deleteActiveMeetingSuccess({ meetingId: meeting._id })),
          catchError((error: unknown) => of(MeetingsPageActions.deleteActiveMeetingFailure({ error })))
        )
      )
    )
  );

  selectMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.selectMeeting),
      map(({ meeting }) => {
        const _id = meeting.previousMeetingId || meeting._id;
        return DetailViewActions.opened({
          config: { pathParts: [DetailType.meeting, _id] },
          source: 'Meeting Selected',
        });
      })
    )
  );

  updateMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.updateMeeting),
      switchMap(action =>
        this.meetingService.updateMeetingHttp(action._id, action.update).pipe(
          map(() => MeetingsPageActions.updateMeetingSuccess()),
          catchError((error: unknown) => of(MeetingsPageActions.updateMeetingFailure({ error })))
        )
      )
    )
  );

  /**
   * Persists pause/resume from the meeting sidenav.
   *
   * Lives here rather than in the component so the PATCH survives the sidenav being destroyed —
   * the request exists precisely so a refresh resumes correctly, and cancelling it on navigation
   * would drop the write. `updateCurrentMeeting` also syncs the store synchronously on dispatch,
   * which is what flips the pause icon.
   */
  persistMeetingPause$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.meetingPauseToggled),
      // Persist the service's OWN sections array, not the payload's copy. `updateCurrentMeeting`
      // Object.assigns onto the live meeting, so any other array replaces the one `currentSection`
      // points into: the section timer would keep ticking an orphaned object while the total (a sum
      // over `sections`) froze. Passing the same reference makes the assign a no-op, and avoids
      // planting the dev-mode frozen action payload on a meeting the service mutates every second.
      mergeMap(({ paused, elapsedTime }) =>
        this.meetingService
          .updateCurrentMeeting({ paused, sections: this.meetingService.currentMeeting?.sections, elapsedTime })
          .pipe(
            map(() => MeetingsPageActions.updateMeetingSuccess()),
            catchError((error: unknown) => of(MeetingsPageActions.updateMeetingFailure({ error })))
          )
      )
    )
  );

  /**
   * Tells the other clients in the meeting that the presenter paused or resumed.
   *
   * Separate from {@link persistMeetingPause$} so a failed PATCH doesn't suppress the broadcast (or
   * vice versa) — they fail independently, exactly as the two bare subscribes in the control did.
   * `emitterId` comes from the store rather than the payload: receivers use it to ignore their own
   * message, and unlike the elapsed times it can't be stale.
   */
  broadcastMeetingPause$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.meetingPauseToggled),
        concatLatestFrom(() => this.store.select(selectCurrentUserId)),
        mergeMap(([{ paused, elapsedTime, currentSection }, emitterId]) =>
          this.meetingService.broadcastMessage({
            messageType: 'meeting',
            document: {
              action: paused ? MeetingMessageAction.Pause : MeetingMessageAction.Continue,
              emitterId,
              meetingElapsedTime: elapsedTime,
              stageElapsedTime: currentSection.elapsedTime,
              stage: currentSection.name,
            },
          })
        )
      ),
    { dispatch: false }
  );

  /**
   * Moves the other clients along when the presenter changes section.
   *
   * The presenter gate lives here rather than at the call site so both entry points — the skip
   * buttons and the section list — can't drift apart on who broadcasts. Attendees navigate their
   * own client only.
   */
  broadcastSectionChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.meetingSectionChanged),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectIsPresenter)),
        filter(([, isPresenter]) => isPresenter),
        mergeMap(([{ stage, previousStage, meetingElapsedTime, stageElapsedTime, stageOrdinal }]) =>
          this.meetingService.broadcastMessage({
            messageType: 'change-stage',
            document: { meetingElapsedTime, stageElapsedTime, stage, previousStage, stageOrdinal },
          })
        )
      ),
    { dispatch: false }
  );

  incrementTangentClickCount$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.tangentButtonClicked),
        concatMap(() =>
          this.store.select(MeetingsStateSelectors.selectCurrentMeeting).pipe(
            take(1),
            switchMap(meeting => {
              if (!meeting?._id) return EMPTY;
              const tangentClickCount = (meeting.tangentClickCount ?? 0) + 1;
              return this.meetingService.updateCurrentMeeting({ tangentClickCount }).pipe(catchError(() => of(null)));
            })
          )
        )
      ),
    { dispatch: false }
  );

  deleteMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteMeeting),
      switchMap(({ meeting }) =>
        this.meetingService.deleteMeeting(meeting).pipe(
          map(() => MeetingsPageActions.deleteMeetingSuccess()),
          catchError((error: unknown) => of(MeetingsPageActions.deleteMeetingFailure({ error })))
        )
      )
    )
  );

  deleteMeetingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteMeetingSuccess),
      map(() => DetailViewActions.close())
    )
  );

  openPrintMeetingAgendas$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.openPrintAgendaDialog),
        tap(() => {
          this.dialog.open(PrintAgendasDialogComponent);
        })
      ),
    { dispatch: false }
  );

  printMeetingAgendas$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingDialogActions.printMeetingAgenda),
        concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectTeamId)]),
        switchMap(([{ meetingType, sortOptions, customAgendaId }, teamId]) =>
          this.printService.openPdf<MeetingAgendaPrintParams>(PrintApi.meetingAgenda, {
            teamId,
            meetingType,
            ...(sortOptions ? { sortOptions } : null),
            printOptions: new PrintOptions(),
            ...(customAgendaId ? { customAgendaId } : {}),
          })
        )
      ),
    { dispatch: false }
  );

  // Recall can't reach a local dev origin, so fall back to this stage's public app origin for
  // local development. /meeting/:meetingId is the live/in-progress session route (same one "Join"
  // on the meetings list uses) - intentional, since the bot posts this link while the meeting is
  // active. Not the /meetings/:type(detail:detail/meeting/:id) completed-meeting route.
  private ninetyMeetingUrlFor(meetingId: string): string {
    const ninetyOrigin = LOCAL_DEV_HOSTNAMES.includes(window.location.hostname)
      ? environment.publicAppOrigin
      : window.location.origin;
    return `${ninetyOrigin}/meeting/${meetingId}`;
  }

  private meetingSettingsHaveChanges(
    team: Team,
    autoArchive: boolean,
    sendMeetingRecapEmail: boolean,
    acceptPendingAgreementBasedTodos: boolean,
    showIssueDescOnRecapEmail: boolean
  ): boolean {
    const { settings } = team;
    return (
      settings?.autoArchive !== autoArchive ||
      settings?.sendMeetingRecapEmail !== sendMeetingRecapEmail ||
      settings?.acceptPendingAgreementBasedTodos !== acceptPendingAgreementBasedTodos ||
      settings?.showIssueDescOnRecapEmail !== showIssueDescOnRecapEmail
    );
  }

  triggerSaveNewCustomAgenda$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.triggerSaveNewCustomAgenda),
        tap(_ => this.agendaV2Service.triggerSaveNewCustomAgenda())
      ),
    { dispatch: false }
  );

  saveNewCustomAgenda_updateTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsAgendasV2Actions.saveNewCustomAgenda),
      map(({ update }) => {
        return TeamListStateActions.updateTeamMeetingAgenda({ update });
      })
    )
  );

  saveNewCustomAgenda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsAgendasV2Actions.saveNewCustomAgenda),
      map(({ update }) => {
        const customAgendas = update.teamAgenda as MeetingAgenda[];
        const newCustomAgenda = customAgendas[customAgendas.length - 1];
        return MeetingsAgendasV2Actions.saveNewCustomAgendaSuccess({ agenda: newCustomAgenda });
      })
    )
  );

  saveNewCustomAgendaSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.saveNewCustomAgendaSuccess),
        tap(({ agenda }) => {
          this.router.navigate([`/${MeetingRoutes.home}/${MeetingRoutes.agendas}/${agenda._id}`]);
        })
      ),
    { dispatch: false }
  );

  triggerUpdateAgenda$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.triggerUpdateAgenda),
        tap(_ => this.agendaV2Service.triggerUpdateAgenda())
      ),
    { dispatch: false }
  );

  updateAgenda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsAgendasV2Actions.updateAgenda),
      map(({ update }) => TeamListStateActions.updateTeamMeetingAgenda({ update }))
    )
  );

  triggerDeleteCustomAgenda$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.triggerDeleteCustomAgenda),
        tap(_ => this.agendaV2Service.triggerDeleteCustomAgenda())
      ),
    { dispatch: false }
  );

  deleteCustomAgenda$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsAgendasV2Actions.deleteCustomAgenda),
      map(({ teamId, agendaId }) => TeamListStateActions.deleteCustomAgenda({ teamId, agendaId }))
    )
  );

  redirectOnDeleteCustomAgendaSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamListStateActions.deleteCustomAgendaSuccess),
        tap(() => {
          this.router.navigate([`/${MeetingRoutes.home}/${MeetingRoutes.agendas}`]);
        })
      ),
    { dispatch: false }
  );

  triggerDiscardChanges$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.triggerDiscardChanges),
        tap(_ => this.agendaV2Service.triggerDiscardChanges())
      ),
    { dispatch: false }
  );

  triggerResetAgenda$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsAgendasV2Actions.triggerResetAgenda),
        tap(_ => this.agendaV2Service.resetAgenda())
      ),
    { dispatch: false }
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingsAgendasV2Actions.saveNewCustomAgendaSuccess,
        MeetingsAgendasV2Actions.saveNewCustomAgendaFailure,
        MeetingsAgendasV2Actions.updateAgendaSuccess,
        MeetingsAgendasV2Actions.updateAgendaFailure,
        TeamListStateActions.updateTeamMeetingAgendaError
      ),
      map(({ type }) => SpinnerActions.stopPrimary({ source: type }))
    )
  );

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingsAgendasV2Actions.saveNewCustomAgenda,
        MeetingsAgendasV2Actions.updateAgenda,
        MeetingsAgendasV2Actions.deleteCustomAgenda
      ),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );

  downloadExcelPastMeetings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PastMeetingsPageActions.downloadExcel),
      concatLatestFrom(() => [
        this.store.select(selectCompanyId),
        this.store.select(MeetingsStateSelectors.selectPage),
        this.store.select(MeetingsStateSelectors.selectSort),
        this.store.select(selectLanguage),
        this.store.select(MeetingsStateSelectors.selectFilters),
        this.store.select(MeetingsStateSelectors.selectSelectedTeamName),
      ]),

      switchMap(([_, companyId, page, sort, language, { meetingType, agendaId, teamId }, team]) =>
        this.meetingsV2Service
          .downloadExcelPastMeetings(companyId, teamId, {
            pageIndex: page.index,
            pageSize: page.size,
            sortDirection: sort.direction,
            sortField: sort.field,
            meetingType,
            agendaId,
          })
          .pipe(
            tap(response => {
              const name = `Past_${language.meeting.items}__${team}`;
              const fileName = `${name}_${formatDate(new Date(), 'yyyy-MM-dd', 'en-US')}.xlsx`;
              this.fileService.downloadExcelFile(response, fileName);
            })
          )
          .pipe(
            map(() => PastMeetingsPageActions.downloadExcelSuccess()),
            catchError(() => of(PastMeetingsPageActions.downloadExcelFailure()))
          )
      )
    )
  );

  openClaimPresenterDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.openClaimPresenterDialog),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      exhaustMap(([, language]) => {
        const confirmDeleteDialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(
          ConfirmDialogComponent,
          {
            data: {
              title: `Are you sure?`,
              message: `By claiming this ${language.meeting.item}'s presenter,
                 you now have the sole responsibility of concluding this ${language.meeting.item}.`,
              confirmButtonText: 'Claim Presenter',
              cancelButtonText: 'Cancel',
            },
          }
        );

        return confirmDeleteDialogRef
          .afterClosed()
          .pipe(
            map(claimConfirmed =>
              claimConfirmed ? MeetingStateActions.claimPresenter() : MeetingStateActions.cancelClaimPresenterDialog()
            )
          );
      })
    )
  );

  claimPresenter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.claimPresenter),
        concatLatestFrom(() => [this.store.select(selectCurrentUserId)]),
        tap(([, currentUserId]) => {
          this.meetingService.changePresenter(currentUserId);
        })
      ),
    { dispatch: false }
  );

  notifyPresenterClaimed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.notifyPresenterClaimed),
      concatLatestFrom(({ presenterUserId }) => [
        this.store.select(selectUserNameById(presenterUserId, 'User Not Found')),
        this.store.select(selectLanguage),
      ]),
      filter(([, userName]) => userName !== 'User Not Found'),
      map(([, userName, language]) =>
        NotificationActions.notifyV2({
          message: `${userName} is now the presenter of this ${language.meeting.item}.`,
          params: getDefaultActionParams(),
        })
      )
    )
  );

  // concatMap: switchMap would cancel the first addWorkedIssue when a second completion arrives.
  handleWorkedIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingIssuesEvents.setCompletedState),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      filter(([, currentMeeting]) => !!currentMeeting),
      concatMap(([{ issueId, completed }]) => {
        const serviceCall$ = completed
          ? this.meetingService.addWorkedIssue(issueId)
          : this.meetingService.removeWorkedIssue(issueId);

        return serviceCall$.pipe(
          map(() => MeetingIssuesActions.handleWorkedIssueSuccess()),
          catchError((error: unknown) =>
            of(MeetingIssuesActions.handleWorkedIssueFailure({ error, issueId, completed }))
          )
        );
      })
    )
  );

  handleNewIssueMetrics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.created),
        concatMap(({ intervalCode, teamId }) => this.meetingService.updateMetricsForCreatedIssue(intervalCode, teamId))
      ),
    { dispatch: false }
  );

  handleDeletedIssueMetrics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.deleted),
        concatMap(({ issueId, isShortTerm }) => this.meetingService.updateMetricsForDeletedIssue(issueId, isShortTerm))
      ),
    { dispatch: false }
  );

  handleTeamChangeMetrics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.teamChanged),
        concatMap(({ teamId, isShortTerm }) => this.meetingService.updateMetricsForTeamChange(teamId, isShortTerm))
      ),
    { dispatch: false }
  );

  handleIntervalChangeMetrics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.intervalChanged),
        concatMap(({ issueId, intervalCode }) =>
          this.meetingService.updateMetricsForIntervalChange(issueId, intervalCode)
        )
      ),
    { dispatch: false }
  );

  // Adds an archived issue to workedIssues so it appears in the meeting recap email.
  // Archiving during a meeting counts as "worked" — without this, only issues marked
  // complete via the checkbox would ever reach the recap.
  // Archiving must not change shortTermIssuesMetricsTotalTracked: it is cleanup, not scoring.
  handleArchivedIssueWorkedState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingIssuesEvents.archived),
      filter(({ completed }) => !!completed),
      concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectCurrentMeeting)),
      filter(([, currentMeeting]) => !!currentMeeting?.inProgress),
      concatMap(([{ issueId }]) =>
        this.meetingService.addWorkedIssue(issueId).pipe(
          map(() => MeetingIssuesActions.handleWorkedIssueSuccess()),
          catchError((error: unknown) =>
            of(MeetingIssuesActions.handleWorkedIssueFailure({ error, issueId, completed: true }))
          )
        )
      )
    )
  );

  broadcastSeguePrompt$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.updateSeguePrompt, MeetingStateActions.resetSeguePrompt),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSeguePrompt)),
        tap(([, prompt]) =>
          this.meetingService.broadcastMessage({ messageType: 'segue-prompt', document: { prompt } }).subscribe()
        )
      ),
    { dispatch: false }
  );

  persistSeguePrompt$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.updateSeguePrompt, MeetingStateActions.resetSeguePrompt),
        concatLatestFrom(() => [
          this.store.select(MeetingsStateSelectors.selectSeguePrompt),
          this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        ]),
        concatMap(([, seguePrompt, currentMeeting]) => {
          if (!currentMeeting?._id) return of(null);
          return this.meetingService
            .updateMeetingHttp(currentMeeting._id, { seguePrompt })
            .pipe(catchError((_error: unknown) => of(null)));
        })
      ),
    { dispatch: false }
  );

  updatePresentUsers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.togglePresentUser, MeetingStateActions.markAllPresent),
        concatLatestFrom(() => [
          this.store.select(selectCurrentUserIsObserver),
          this.store.select(MeetingsStateSelectors.selectSegueAttendeeIds),
        ]),
        filter(([, isObserver]) => !isObserver),
        concatMap(([, , segueAttendeeIds]) =>
          this.meetingService.updatePresentUsers(segueAttendeeIds).pipe(catchError((_error: unknown) => of(null)))
        )
      ),
    { dispatch: false }
  );

  persistPresenceJoin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealTimeActions.presenceChanged),
        filter(({ action }) => action === 'join'),
        concatLatestFrom(() => [
          this.store.select(selectCurrentUserIsObserver),
          this.store.select(MeetingsStateSelectors.selectSegueAttendeeIds),
          this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
          this.store.select(MeetingsStateSelectors.selectMeetingStatus),
          this.store.select(MeetingsStateSelectors.selectIsPresenter),
        ]),
        // First filter: skip if observer
        filter(([, isObserver]) => !isObserver),
        // Second filter: presenter only — non-presenters get 403 on meeting update
        filter(([, , , , , isPresenter]) => !!isPresenter),
        // Third filter: ensure channelId matches current meeting and currentMeeting is loaded
        filter(([{ channelId }, , , currentMeeting, meetingStatus]) => {
          const meetingId = currentMeeting?._id ?? meetingStatus?._id;
          return !!meetingId && !!channelId && channelId.includes(meetingId) && !!this.meetingService.currentMeeting;
        }),
        concatMap(([, , segueAttendeeIds]) =>
          this.meetingService.updatePresentUsers(segueAttendeeIds).pipe(catchError((_error: unknown) => of(null)))
        )
      ),
    { dispatch: false }
  );

  toggleVotingEnabled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingIssuesEvents.toggleVotingEnabled),
        concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
        filter(([, currentMeeting]) => !!currentMeeting),
        tap(([{ votingEnabled }]) => {
          this.meetingService.toggleVotingEnabled(votingEnabled);
        })
      ),
    { dispatch: false }
  );

  shuffleSegueOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.getMeetingSuccess),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectIsPresenter),
        this.store.select(MeetingsStateSelectors.selectSegueAttendeeIds),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingPresenterUserId),
      ]),
      filter(([, isPresenter]) => isPresenter),
      map(([, , segueAttendeeIds, presenterUserId]) => {
        const withoutPresenter = segueAttendeeIds.filter(id => id !== presenterUserId);
        // Use lodash.shuffle for unbiased shuffling
        const shuffled = shuffle(withoutPresenter);
        return presenterUserId ? [...shuffled, presenterUserId] : shuffled;
      }),
      switchMap(shuffled =>
        this.meetingService
          .broadcastMessage({ messageType: 'present-users', document: { presentUsers: shuffled } })
          .pipe(
            catchError(() => of(null)),
            map(() => MeetingStateActions.markAllPresent({ userIds: shuffled }))
          )
      )
    )
  );

  // keep sharer index in sync when a user joins - if the presenter is sharing, we want new joiners to have the correct sharer index
  broadcastSharerIndexOnJoin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealTimeActions.presenceChanged),
        filter(({ action }) => action === 'join'),
        concatLatestFrom(() => [
          this.store.select(MeetingsStateSelectors.selectIsPresenter),
          this.store.select(MeetingsStateSelectors.selectSharerIndex),
          this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
          this.store.select(MeetingsStateSelectors.selectMeetingStatus),
        ]),
        filter(([{ channelId }, isPresenter, , currentMeeting, meetingStatus]) => {
          const meetingId = currentMeeting?._id ?? meetingStatus?._id;
          return isPresenter && !!meetingId && !!channelId && channelId.includes(meetingId);
        }),
        switchMap(([, , sharerIndex]) =>
          this.meetingService
            .broadcastMessage({ messageType: 'sharer-index', document: { sharerIndex } })
            .pipe(catchError(() => of(null)))
        )
      ),
    { dispatch: false }
  );

  broadcastSharerIndex$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.advanceSharer, MeetingStateActions.retreatSharer),
        concatLatestFrom(() => this.store.select(MeetingsStateSelectors.selectSharerIndex)),
        switchMap(([, sharerIndex]) =>
          this.meetingService.broadcastMessage({ messageType: 'sharer-index', document: { sharerIndex } }).pipe(
            catchError(() => {
              return of(null);
            })
          )
        )
      ),
    { dispatch: false }
  );

  broadcastSkipSharer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingStateActions.skipSharer),
        concatLatestFrom(() => [
          this.store.select(MeetingsStateSelectors.selectSegueAttendeeIds),
          this.store.select(MeetingsStateSelectors.selectSharerIndex),
        ]),
        switchMap(([, segueAttendeeIds, sharerIndex]) =>
          this.meetingService
            .broadcastMessage({ messageType: 'present-users', document: { presentUsers: segueAttendeeIds } })
            .pipe(
              switchMap(() =>
                this.meetingService.broadcastMessage({ messageType: 'sharer-index', document: { sharerIndex } })
              ),
              catchError(() => of(null))
            )
        )
      ),
    { dispatch: false }
  );

  trackSegueNextSpeaker$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.advanceSharer),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.SEGUE_NEXT_SPEAKER_CLICKED }))
    )
  );

  trackSeguePromptEdited$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.updateSeguePrompt),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.SEGUE_PROMPT_EDITED }))
    )
  );

  trackSegueAttendeeToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.togglePresentUser),
      map(({ userId }) => SegmentActions.track({ event: SegmentTrackEvent.SEGUE_ATTENDEE_TOGGLED, params: { userId } }))
    )
  );

  // Fires when the facilitator clicks "Mark All Present" to set every attendee as present.
  // Note: this also fires when shuffleSegueOrder$ internally dispatches markAllPresent on meeting load —
  // that is intentional, as it indicates the presenter started the Segue with all users present.
  trackSegueMarkAllPresent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingStateActions.markAllPresent),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.SEGUE_MARK_ALL_PRESENT }))
    )
  );

  // Fires only on the joining user's own client when they navigate to a meeting that is in the Segue section.
  // PubNub presence events fire on every connected client, so we use userId === currentUserId to ensure
  // only the user who actually joined on their own device fires the event (not the facilitator's client).
  // Exact channel match (meeting-{id}) prevents false positives from other meeting channels the user
  // may still be subscribed to from a previous session.
  trackSegueUserJoinedOwnDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.presenceChanged),
      filter(({ action }) => action === 'join'),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentSection),
        this.store.select(selectCurrentUserId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(MeetingsStateSelectors.selectMeetingStatus),
      ]),
      filter(([{ userId, channelId }, currentSection, currentUserId, currentMeeting, meetingStatus]) => {
        const meetingId = currentMeeting?._id ?? meetingStatus?._id;
        return (
          currentSection?.path === 'segue' &&
          userId === currentUserId &&
          !!meetingId &&
          channelId === `meeting-${meetingId}`
        );
      }),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.SEGUE_USER_JOINED_OWN_DEVICE }))
    )
  );
}
